/**
 * src/hooks/useMyActionItems.js
 *
 * The viewer's action items — listMyActionItems (the one shared, pure
 * aggregation) bound to React. The ◎ badge (useActionCount) and the Action
 * Centre list both read this, so badge === items.length (ACEA-01).
 */

import { useMemo } from 'react';
import { listMyActionItems } from '../selectors/myActionItems.js';
import { useMyBugUpdates } from './useMyBugUpdates.js';
import { useMyCommQuestions, useMyCommQuestionsStatus } from './useMyCommQuestions.js';
import {
  useMyWorkflowSteps,
  useMyWorkflowStepsFailed,
  useMyHaltReviews,
  useMyHaltReviewsFailed,
} from './useMyWorkflowSteps.js';
import {
  useRiskAiAssessmentList,
  useRiskAiAssessmentListFailed,
} from './useRiskAiAssessmentList.js';

const NO_TASKS = [];

/**
 * @param {object} state
 * @param {object} session
 * @param {Array}  users
 * @param {object} [extras] { obligationTasks, tenant, refreshBugsOnMount }
 */
export function useMyActionItems(state, session, users = [], extras = {}) {
  const { obligationTasks = NO_TASKS, tenant = null, refreshBugsOnMount = false } = extras;
  const {
    bugUpdates,
    failed: bugUpdatesFailed,
    acknowledgeBug,
  } = useMyBugUpdates(session, tenant, {
    refreshOnMount: refreshBugsOnMount,
  });
  const commQuestions = useMyCommQuestions(session, tenant, { refreshOnMount: refreshBugsOnMount });
  // The viewer's open workflow steps (PDWB-02) — Tier 2 tasks in the one list.
  const workflowSteps = useMyWorkflowSteps(session, tenant, { refreshOnMount: refreshBugsOnMount });
  // The viewer's open reviews of halted recurring workflows (PDW-HALT-2).
  const haltReviews = useMyHaltReviews(session, tenant, { refreshOnMount: refreshBugsOnMount });
  // Undecided AI risk assessments (RAI-05) — "decide on AI assessment" tasks.
  const riskAiAssessments = useRiskAiAssessmentList(session, tenant, {
    refreshOnMount: refreshBugsOnMount,
  });
  // GH #8721: a summed list whose read failed is unknown, never 0 — so the
  // badge can say its count is a lower bound instead of "nothing owed".
  const workflowStepsFailed = useMyWorkflowStepsFailed(session, tenant);
  const riskAiFailed = useRiskAiAssessmentListFailed(session, tenant);
  const haltReviewsFailed = useMyHaltReviewsFailed(session, tenant);
  const questionsFailed = useMyCommQuestionsStatus(session, tenant) === 'failed';
  const incomplete =
    workflowStepsFailed || haltReviewsFailed || riskAiFailed || questionsFailed || bugUpdatesFailed;
  const userId = session?.userId;
  const role = session?.role;
  const hasState = !!state;

  const items = useMemo(
    () =>
      listMyActionItems(state, { userId, role }, users, {
        obligationTasks,
        bugUpdates,
        commQuestions,
        workflowSteps,
        haltReviews,
        riskAiAssessments,
      }),
    // Keyed on the state slices the selector reads rather than the whole
    // `state`, so an unrelated update doesn't rerun this O(n·m) aggregation.
    // A slice read by listMyActionItems must be listed here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      hasState,
      userId,
      role,
      users,
      obligationTasks,
      bugUpdates,
      commQuestions,
      workflowSteps,
      haltReviews,
      riskAiAssessments,
      state?.risks,
      state?.riskObligations,
      state?.adhocTasks,
      state?.riskProposals,
      state?.incidentProposals,
      state?.incidents,
      state?.findingProposals,
      state?.gapAnalyses,
      state?.uarCampaigns,
      state?.employees,
      state?.vendors,
      state?.aiSystems,
      state?.aiProhibitedScreenings,
      state?.dpias,
      state?.tias,
      state?.controlOwnership,
      state?.soaData,
      state?.tenantDomains,
      state?.portalSubmissions,
      state?.documents,
      state?.documentAssignments,
      state?.soaVersions,
      state?.soaSettings,
      state?.biaAssessments,
      state?.biaReviews,
      state?.biaReviewCycles,
      state?.findings,
      state?.evidenceRequests,
      state?.managementReviews,
      state?.artefacts,
    ]
  );

  return { items, incomplete, bugUpdatesFailed, acknowledgeBug };
}
